import trustAzure from '../assets/v2-trust-azure.png';
import trustMarketplace from '../assets/v2-trust-marketplace.png';
import maskSaas from '../assets/v2-trust-saas-mask.png';
import maskSecure from '../assets/v2-trust-secure-mask.png';
import maskSecurity from '../assets/v2-trust-security-mask.png';

/**
 * Trust / partner strip — Figma node 2:47. NEW in the 2026-07 refresh.
 *
 * Design values (at the 1920 reference width):
 *  · band   px-133 py-46; inner row px-168, space-between, w-1920
 *  · item   flex, gap-10, items-center; icon 68×68
 *  · label  Inter 700 18px #111827
 *  · "Available on" is Inter 500 18px #717171 above its bold label, gap-10
 *
 * The first two icons are full-colour images. The last three are alpha masks
 * tinted #E87722 in the design (a solid fill masked by the shape), reproduced
 * here with `mask-image` over a `bg-primary-500` box — #E87722 is exactly
 * primary-500, so the brand token is used rather than a raw hex.
 *
 * The 68px icon and 18px label are fixed sizes, so below `lg` the five items
 * wrap into a centred grid instead of stretching a single row past the viewport.
 */

interface TrustItem {
  label: string;
  /** Rendered above the label in muted grey, as on the Azure Marketplace item. */
  eyebrow?: string;
  /** Full-colour image, used by the first two items. */
  image?: string;
  /** Alpha mask tinted with the brand orange, used by the last three. */
  mask?: string;
}

const ITEMS: readonly TrustItem[] = [
  { label: 'Microsoft Azure', image: trustAzure },
  { label: 'Azure Marketplace', eyebrow: 'Available on', image: trustMarketplace },
  { label: 'Enterprise SaaS', mask: maskSaas },
  { label: 'Secure by Design', mask: maskSecure },
  { label: 'Enterprise Security', mask: maskSecurity },
];

/**
 * Partnerships row — appears under the 5 trust signals. Kept as an array so
 * more programs can drop in with a single line each; the row hides itself if
 * the array is empty.
 */
interface Partnership {
  /** Program name — rendered bold inside the pill. */
  program: string;
}

const PARTNERSHIPS: readonly Partnership[] = [
  { program: 'WisdomX Orbis is an active SAP PartnerEdge Open Ecosystem Build Partner' },
];

function TrustIcon({ item }: { item: TrustItem }) {
  if (item.image) {
    return (
      <img
        src={item.image}
        alt=""
        aria-hidden="true"
        width={68}
        height={68}
        className="block size-12 shrink-0 object-contain lg:size-[68px]"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="block size-12 shrink-0 bg-primary-500 lg:size-[68px]"
      style={{
        maskImage: `url(${item.mask})`,
        WebkitMaskImage: `url(${item.mask})`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  );
}

export function TrustStrip() {
  return (
    <section
      aria-label="Platform credentials"
      className="w-full bg-white px-5 py-8 sm:px-6 md:px-8 lg:px-10 lg:py-[46px] xl:px-16 2xl:px-[133px]"
    >
      <ul className="mx-auto flex max-w-[1584px] list-none flex-wrap items-center justify-center gap-x-8 gap-y-6 p-0 lg:justify-between lg:gap-x-4">
        {ITEMS.map((item) => (
          <li key={item.label} className="flex items-center gap-2.5">
            <TrustIcon item={item} />
            {item.eyebrow ? (
              <span className="flex flex-col gap-1 lg:gap-2.5">
                <span className="text-sm font-medium text-landing-grey lg:text-lg">
                  {item.eyebrow}
                </span>
                <span className="text-sm font-bold text-landing-body lg:text-lg">{item.label}</span>
              </span>
            ) : (
              <span className="text-sm font-bold text-landing-body lg:text-lg">{item.label}</span>
            )}
          </li>
        ))}
      </ul>

      {/* Plain wordmark line — no pill fill, no dot, no checkmark. "Build
          Partner" is a programme tier, not a certification, so this row must
          not visually borrow the "verified/certified" language of the icon
          row above. Kept as separate <li>s + a middle dot separator so more
          partnerships can be added without changing the treatment. */}
      {PARTNERSHIPS.length > 0 && (
        <div className="mx-auto mt-6 max-w-[1584px] border-t border-landing-footer-line pt-5 lg:mt-8 lg:pt-6">
          <ul className="flex list-none flex-wrap items-center justify-center gap-x-2 gap-y-1 p-0 text-center text-sm text-landing-grey lg:text-base">
            {PARTNERSHIPS.map((p, i) => (
              <li key={p.program} className="inline-flex items-center gap-2">
                {i > 0 && (
                  <span aria-hidden="true" className="text-landing-grey/60">
                    ·
                  </span>
                )}
                <span>{p.program}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
